<script setup>
import { reactive, ref } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import JetActionSection from '@/Jetstream/ActionSection.vue';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal.vue';
import JetDangerButton from '@/Jetstream/DangerButton.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';

// includes we need to front end locale magic
import { useI18n } from 'vue-i18n';
// Get locale as set in cookie
let { locale } = useI18n({ useScope: 'global' });

const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const urlNamePrefix = organisation ? "gi." : "";
const userID = router.page.props.auth.user.id;

const props = defineProps({
    team: Object,
    availableRoles: Array,
    loggedInUserId: Number,
    teamDeletionData: Object,
    loggedInUserRole: Object,
});

const loggedInUserRole = props.loggedInUserRole;
//console.log('Role: ' + loggedInUserRole);

let team_custom_name = programme.team_custom_name;
if (team_custom_name) {
    if(team_custom_name.constructor.name != 'Object') {
        team_custom_name = JSON.parse(team_custom_name);
    }
}
        
let default_team_name;

if (locale.value == 'cy') {
    default_team_name = 'tîm';
    
} else  {
    default_team_name = 'team';
}

let team_name = default_team_name;

if (team_custom_name) {
    team_name =  team_custom_name[locale.value] == null ? default_team_name : team_custom_name[locale.value];
}

const isChecked = ref(false);

const teamScheduledForDeletion = ref(false);
if ( props.team.scheduled_for_deletion === 1 ) {
    teamScheduledForDeletion.value = true;
}

const currentDate = new Date();
let teamDeletionData = ref(props.teamDeletionData);

function formatDateString(dateString) {
  // Create a Date object from the string
  const date = new Date(dateString);

  // Get the parts of the date
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  // Format the date
  return `${day} ${month} ${year}`;
}

function getDatePlusDays(dateString, daysToAdd) {
  // Create a Date object from the string
  const date = new Date(dateString);

  // Add the specified number of days
  date.setDate(date.getDate() + daysToAdd);

  // Get the parts of the new date
  const newDay = date.getDate();
  const newMonth = date.toLocaleString('default', { month: 'long' });
  const newYear = date.getFullYear();

  // Format the new date
  return `${newDay} ${newMonth} ${newYear}`;
}


const confirmingTeamDeletion = ref(false);
const confirmingDeleteAction = ref(true);


const form = useForm({});

const confirmTeamDeletion = () => {
    confirmingTeamDeletion.value = true;
};

const confirmDeleteAction = () => {
    confirmingDeleteAction.value = !confirmingDeleteAction.value;
};


const deleteTeam = () => {
    if (urlNamePrefix == 'gi.') {
        form.delete(route(urlNamePrefix+'teams.destroyGI', props.team), {
            errorBag: 'deleteTeam',
        });
    }
    else {
        form.delete(route(urlNamePrefix+'teams.destroy', props.team), {
            errorBag: 'deleteTeam',
        }); 
    }

};

const scheduleTeamDeletion = () => {
    form.post(route(urlNamePrefix+'teams.scheduleDeletion', props.team) + '?userID=' + userID, {
        errorBag: 'scheduleTeamDeletion',
        onSuccess: function() {
            //console.log('Success!');
            if ( teamScheduledForDeletion.value == false ) {
                // This means you've just scheduled this team for deletion.
                teamScheduledForDeletion.value = true;
                //console.log(props.teamDeletionData);   
                teamDeletionData = props.teamDeletionData;
                //console.log('You have scheduled this team for deletion. teamScheduledForDeletion now set to ' + teamScheduledForDeletion.value);
            }
            else {
                teamScheduledForDeletion.value = false; 
                confirmingTeamDeletion.value = false;
                //console.log(props.teamDeletionData);
                //console.log('You have cancelled deletion for this team  teamScheduledForDeletion now set to ' + teamScheduledForDeletion.value);
            }
            confirmingTeamDeletion.value = false;
            confirmingDeleteAction.value = true;
            isChecked.value = false;
        },
        onError: (errors) => {
            //console.log('Fail!');
        }
    });
};

</script>

<template>
    <JetActionSection>
        <template #title>
            {{ $t('teams.delete.title', { team_name: team_name }) }}
        </template>

        <template #description>
            {{ $t('teams.delete.description', { team_name: team_name }) }}
        </template>

        <template #content>
            <div v-if="teamScheduledForDeletion && teamDeletionData.length > 0"  class="max-w-xl text-sm text-gray-600">
                <h2>
                    
                    {{ $t('teams.delete.scheduled_for_deletion_title', { team_name: team_name }) }}
                </h2>
            </div>
            <div v-else-if="teamScheduledForDeletion && teamDeletionData.length == 0" class="mt-5">
                {{ $t('teams.delete.scheduled_for_deletion_title', { team_name: team_name }) }}
            </div>
            <div v-else-if="loggedInUserRole == 'admin'">
                {{ $t('teams.delete.warning', { team_name: team_name }) }}
            </div>
            <div v-else>
                {{ $t('teams.delete.no_delete_permission') }}
            </div>

            <div v-if="teamScheduledForDeletion && teamDeletionData.length > 0" class="mt-5">
                <p v-html="$t('teams.delete.scheduled_deletion_date_text', { scheduled_deletion_date: formatDateString(teamDeletionData[0].scheduled_deletion_date), team_name: team_name })"></p>
                <JetDangerButton @click="scheduleTeamDeletion">
                    {{ $t('teams.delete.cancel_scheduled_deletion', { team_name: team_name }) }}
                </JetDangerButton>
            </div>
            <div v-else-if="teamScheduledForDeletion && teamDeletionData.length == 0" class="mt-5">
                <p v-html="$t('teams.delete.scheduled_deletion_date_text', { scheduled_deletion_date: getDatePlusDays(currentDate, 7), team_name: team_name })"></p>
                <JetDangerButton @click="scheduleTeamDeletion">
                    {{ $t('teams.delete.cancel_scheduled_deletion', { team_name: team_name }) }}
                </JetDangerButton>
            </div>
            <div v-else-if="loggedInUserRole == 'admin'" class="mt-5">
                <JetDangerButton @click="confirmTeamDeletion">
                    {{ $t('teams.delete.confirm', { team_name: team_name }) }}
                </JetDangerButton>
            </div>

            <!-- Delete Team Confirmation Modal -->
            <JetConfirmationModal :show="confirmingTeamDeletion" @close="confirmingTeamDeletion = false">
                <template #title>
                    {{ $t('teams.delete.title', { team_name: team_name }) }}
                </template>

                <template #content>
                    <p class="mt-5">{{ $t('teams.delete.confirmation1', { team_name: team_name }) }}</p>
                    <p class="mt-5">{{ $t('teams.delete.confirmation2', { team_name: team_name }) }}</p>
                    <p class="mt-5">{{ $t('teams.delete.confirmation3', { team_name: team_name }) }}</p>
                    <p class="mt-5">{{ $t('teams.delete.confirmation4', { team_name: team_name }) }}</p>

                    <p class="mt-5">
                        <JetCheckbox
                        class="ml-3"
                        :class="{ 'opacity-25': form.processing }"
                        v-model="isChecked" 
                        :id="'checkbox'" 
                        :value="'1'"
                        @click="confirmDeleteAction"
                    >
                
                    </JetCheckbox>
                    <label for="checkbox"> {{ $t('teams.delete.confirmation_checkbox_label', { team_name: team_name }) }}.</label>
                    </p>
                </template>

                <template #footer>
                    <JetSecondaryButton @click="confirmingTeamDeletion = false">
                        {{ $t('teams.delete.cancel') }}
                    </JetSecondaryButton>
                    <JetDangerButton
                        class="ml-3"
                        :class="{ 'opacity-25': form.processing }"
                        :disabled="confirmingDeleteAction"
                        @click="scheduleTeamDeletion"
                    >
                        {{ $t('teams.delete.confirm') }}
                    </JetDangerButton>
                </template>
            </JetConfirmationModal>
        </template>
    </JetActionSection>
</template>
